import { Injectable } from '@angular/core';

// external libs
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

// services
import { MapManager } from '../manager/map-manager';
import { MarkerClusterLoadService } from './h21-map-marker-cluster-load.service';
import { MarkerClusterEventService } from './h21-map-marker-cluster-event.service';

// directives
import { H21MapMarkerClusterDirective } from '../directives/h21-map-marker-cluster.directive';

// enums
import { GeoSeachType } from '../enums/geo-search-type.enum';
import { ChangesMap } from '../enums/changes-map.enum';

// models
import { GeoBoundBox } from '../models/geo-bound-box.model';
import { GeneralInfoFilter } from '../models/general-info-filter.model';
import { Query } from '../models/query.model';
import { GeoPolygon } from '../models/geo-polygon.model';
import { AggregationFilter } from '../models/aggregation-filter.model';
import { CircleEvent } from '../models/circle-event.model';
import { AreaEvent } from '../models/area-event.model';
import { GeoCircle } from '../models/geo-circle.model';
import { Bounds } from '../models/bounds-map.model';
import { MarkerClusterFilter } from '../models/marker-cluster-filter.model';
import { MarkerClusterFind } from '../models/marker-cluster-find.model';

@Injectable()
export class H21MapClusterService {

    public layer: H21MapMarkerClusterDirective;
    public manager: MapManager;

    public changeMap: ChangesMap;
    public maxZoom: number;

    public circle: CircleEvent;
    public area: AreaEvent;
    public bounds: Bounds;
    public currentZoom: number;
    public isPending = true;
    public filter = null;
    public requestId: string;

    public destroy$ = new Subject<boolean>();
    public pending$ = new Subject<boolean>();

    constructor(
        private _eventService: MarkerClusterEventService,
        private _loadService: MarkerClusterLoadService,
    ) {
        this._eventService.cmp = this;
        this._loadService.cmp = this;
    }

    public destroy(): void {
        this.currentZoom = null;
        this.filter = null;
        this.circle = null;
        this.area = null;
        this.bounds = null;

        this.destroy$.next(true);
        this.destroy$.complete();

        this.pending$.next(true);
        this.pending$.complete();
    }

    public initService(manager: MapManager, layer: H21MapMarkerClusterDirective): void {
        this.manager = manager;
        this.layer = layer;
        this.maxZoom = layer.maxZoom;
        this.manager.getMap().drawing.drawingManager && this._eventService.addEventListenerArea();
        this.manager.getMap().drawing.drawingManager && this._eventService.addEventListenerCircle();
        this._eventService.addEventListenerZoom();
        this._eventService.addEventListenerFilter();
        this._eventService.addEventListenerBounds();
        this._eventService.addEventListenerTilesLoad();
        this._eventService.addEventListenerIsMarkerCluster();
        this.setChangeMap(layer);
        this.manager.getMap().markerCluster.clearMarkerCluster();
    }

    public updateService(manager: MapManager, layer: H21MapMarkerClusterDirective): void {
        this.manager = manager;
        this.layer = layer;
    }

    public setFilter(layer: H21MapMarkerClusterDirective): void {
        this.filter = layer.filter;
        this.manager.getMap().markerCluster.removeFeature(this.layer);
        this.filter.responseId && this.getGeoJson(this.buildFilter(this.filter));
    }

    public setChangeMap(layer: H21MapMarkerClusterDirective): void {
        this.changeMap = layer.changesMap;
    }

    public setMaZoom(layer: H21MapMarkerClusterDirective): void {
        this.maxZoom = layer.maxZoom;
    }

    public buildFilterCircle(event: CircleEvent): Query<GeneralInfoFilter> {
        const zoom = this._zoomConversion(event.zoom);
        this.currentZoom = event.zoom;
        return new Query<GeneralInfoFilter>({
            geoSearchFilter: new GeneralInfoFilter({
                searchType: GeoSeachType.circle,
                circle: new GeoCircle({
                    center: event.position,
                    radius: event.radius,
                }),
            }),
            aggregationFilter: new AggregationFilter({
                precision: `Precision${zoom}`,
                clusterMinCount: this.layer.minClusterSize,
            }),

        });
    }

    public buildFilterBoundBox(event: Bounds): Query<GeneralInfoFilter> {
        return new Query<GeneralInfoFilter>({
            geoSearchFilter: new GeneralInfoFilter({
                searchType: GeoSeachType.boundingBox,
                boundingBox: new GeoBoundBox({
                    bottomLeft: event.sw,
                    topRight: event.ne,
                }),
            }),

            aggregationFilter: new AggregationFilter({
                precision: `Precision${event.zoom}`,
                clusterMinCount: this.layer.minClusterSize,
            }),
        });
    }

    public buildFilterPolygon(event: AreaEvent): Query<GeneralInfoFilter> {
        const zoom = this._zoomConversion(event.zoom);
        this.currentZoom = event.zoom;
        return new Query<GeneralInfoFilter>({
            geoSearchFilter: new GeneralInfoFilter({
                searchType: GeoSeachType.multipolygon,
                multipolygon: new GeoPolygon({
                    points: event.position,
                }),
            }),

            aggregationFilter: new AggregationFilter({
                precision: `Precision${zoom}`,
                clusterMinCount: this.layer.minClusterSize,
            }),
        });
    }

    public buildFilter(filter: MarkerClusterFilter, zoom?: number): Query<GeneralInfoFilter> {
        const currentZoom = this._zoomConversion(zoom || this.manager.getMap().getZoom());
        return new Query<GeneralInfoFilter>({
            searchFilter: new GeneralInfoFilter({
                responseId: filter.responseId,
                starRatingIn: filter.starRatingIn,
                facilitiesIn: filter.facilitiesIn,
                carbonFootprintIn: filter.carbonFootprintIn,
            }),

            aggregationFilter: new AggregationFilter({
                precision: `Precision${currentZoom}`,
                clusterMinCount: this.layer.minClusterSize,
            }),

            maxZoom: this.maxZoom && zoom >= this.maxZoom,
        });
    }

    public buildIsMarkerCluster(event: MarkerClusterFind): GeneralInfoFilter {
        return new GeneralInfoFilter({
            showOnMapRequestId: event.showOnMapRequestId,
            location: event.location,
        });
    }

    public getGeoJson(query: Query<GeneralInfoFilter>): void {
        this.manager.getMap().markerCluster.loading.next(true);
        const byFilter$ = this._loadService.loadGeoJson(query).pipe(
            debounceTime(800),
            takeUntil(this.pending$));
        byFilter$.subscribe((geoJson: GeneralInfoFilter[]) => {
            this.manager.getMap().markerCluster.removeFeature(this.layer);
            this.pending$.next(false);
            this.isPending = true;
            this.layer.geoJson = geoJson;
            this.manager.getMap().markerCluster.updateMarkerCluster(this.layer);
            this.manager.getMap().markerCluster.loading.next(false);
        });
    }

    public getGeoJsonFilter(query: Query<GeneralInfoFilter>): void {
        this.manager.getMap().markerCluster.loading.next(true);
        const byFilter$ = this._loadService.loadGeoJsonFilter(query).pipe(
            debounceTime(1000),
            takeUntil(this.pending$));
        byFilter$.subscribe((geoJson: any) => {
            this.manager.getMap().markerCluster.removeFeature(this.layer);
            this.pending$.next(false);
            this.isPending = true;
            this.requestId = geoJson.showOnMapRequestId;
            this.layer.geoJson = geoJson.geoJsonGeoResponse;
            this.manager.getMap().markerCluster.updateMarkerCluster(this.layer);
            this.manager.getMap().markerCluster.loading.next(false);
        });
    }


    public getIsMarkerCluster(query: GeneralInfoFilter): void {
        this.manager.getMap().markerCluster.loading.next(true);
        const byFilter$ = this._loadService.isMarkerCluster(query).pipe(
            debounceTime(1000),
            takeUntil(this.pending$));
        byFilter$.subscribe((featureId: any) => {
            this.pending$.next(false);
            this.manager.getMap().markerCluster.updateStyle(featureId);
        });
    }

    private _zoomConversion(zoom: number): number {
        return zoom < 11 ? zoom = 3 : zoom = zoom;
    }

}

